import { isNode } from "@use/is-node";
import { hrtime } from "@use/universal-hrtime";
import { clearTimeout, setTimeout } from "@use/universal-timeout";

/**
 * The EntityStore creates an interface to maintain a memory cache pools, which
 * will automatically perform garbage collections to meet the restrictions on
 * the store.
 *
 * This allows long-lived applications to prevent memory usage from bloating
 * over time, as old entities will automatically be freed.
 */
export class EntityStore<T> {
    /**
     * Specifies if we should enable the store in a browser.
     */
    private readonly browser: boolean;

    /**
     * Specifies the maximum capcity of the store (if more entires are added,
     * then the entries that were last accessed longest time ago will
     * automatically be deleted).
     */
    private readonly capacity: number;

    /**
     * Specifies if we should enable the store in a node.js environment.
     */
    private readonly node: boolean;

    /**
     * Specifies how long entities in the store survives without having been
     * accessed; that way an entity will automatically be deleted if it hasn't
     * been read or updated after this "time to idle".
     *
     * The value is given in seconds.
     */
    private readonly tti?: number;

    /**
     * Specifies how long entities in the store survives after having been put
     * into the store; that way an entity will automatically be deleted if it
     * hasn't been updated after this "time to live".
     *
     * The value is given in seconds.
     */
    private readonly ttl?: number;

    /**
     * Internal map containing stored entries.
     */
    private readonly store: Map<string, T> = new Map<string, T>();

    /**
     * Internal map containing timestamps for the last time that entities in the
     * store was read.
     */
    private readonly lastRead: Map<string, number> = new Map<string, number>();

    /**
     * Internal map containing timestamps for the last time that entities in the
     * store was written.
     */
    private readonly lastWrite: Map<string, number> = new Map<string, number>();

    /**
     * Internal reference to timeouts used to run garbage collection once a
     * minute.
     */
    private garbageCollectionTimeout: number | NodeJS.Timeout;

    /**
     * @param config.browser If set to false, then thhe entity store will not be
     *        used in a browser (ie. we will never write anything to it).
     * @param config.capacity The maximum capacity of the store specifies a
     *        threshold, which will never be crossed. If the capacity is reached
     *        the store will automatically dispose of the oldest entries.
     * @param config.node If set to false, then thhe entity store will not be
     *        used in a node.js environment (ie. we will never write anything to
     *        it).
     * @param config.tti The time to idle for entities in seconds. If any of the
     *        entities remain unread for longer than this duration they're
     *        automatically marked for garbage collection.
     * @param config.ttl The time to live for entities in seconds. If an entity
     *        hasn't been updated for longer than this duration, they're
     *        automatically marked for garbage collection. If both tti and ttl
     *        is given, then ttl will take precendence.
     */
    constructor({
        browser = true,
        capacity = 1000,
        node = true,
        tti,
        ttl,
    }: {
        browser?: boolean;
        capacity?: number;
        node?: boolean;
        tti?: number;
        ttl?: number;
    }) {
        this.browser = browser;
        this.capacity = capacity;
        this.node = node;
        this.tti = tti !== undefined && ttl === undefined ? tti * 1000 : undefined;
        this.ttl = ttl !== undefined ? ttl * 1000 : undefined;

        // Default to a tti of 1800 seconds
        if (this.tti === undefined && this.ttl === undefined) {
            this.tti = 1800000;
        }

        // Start garbage collection once every minute
        this.garbageCollectionTimeout = setTimeout(this.garbageCollect, 60000);
    }

    /**
     * Will return the current size of the store (ie. count of entities
     * currently held within it).
     */
    public get size(): number {
        return this.store.size;
    }

    /**
     * Store the entity inside the store for future usage.
     *
     * NOTE that the entity can be removed from the store at any time, if the
     * garbage collector decides that it's time to do so.
     */
    public set(key: string, entity: T): void { // tslint:disable-line no-reserved-keywords
        // Should we bail out based on given environment?
        if (isNode()) {
            if (!this.node) {
                return;
            }
        } else {
            if (!this.browser) {
                return;
            }
        }

        this.store.set(key, entity);
        this.lastRead.set(key, hrtime());
        this.lastWrite.set(key, hrtime());

        // If the store is full, then perform garbage collection
        if (this.store.size > this.capacity) {
            this.garbageCollect();
        }
    }

    /**
     * Gets the entity cached under the given key from the store
     */
    public get(key: string): T | undefined { // tslint:disable-line no-reserved-keywords
        // Update access time
        this.lastRead.set(key, hrtime());

        // Return value
        return this.store.get(key);
    }

    /**
     * Determines if the store holds an entity under the specified key
     */
    public has(key: string): boolean {
        // Update access time
        this.lastRead.set(key, hrtime());

        // Return result
        return this.store.has(key);
    }

    /**
     * Deletes the entity held under the specified key from the store
     */
    public delete(key: string): void { // tslint:disable-line no-reserved-keywords
        this.lastRead.delete(key);
        this.lastWrite.delete(key);
        this.store.delete(key);
    }

    /**
     * This internal routine loops through all entries and will automatically
     * ensure that the store is kept within the thresholds specified.
     *
     * It starts by identifying all entries that have not been accessed for
     * longer than the specified time to live, and then removes them from the
     * cache.
     *
     * Afterwards it will determine if we're currently exceeding the capacity of
     * the store. In that case it will automatically delete the oldest entries
     * until the capacity threshold is met again.
     */
    private garbageCollect = () => {
        // Loop through and destroy all items that have remained unread for
        // longer than the specified "time to idle"
        if (this.tti !== undefined && this.ttl === undefined) {
            const tstampThreshold = hrtime() - this.tti;

            this.lastRead.forEach((lastRead, key) => {
                if (lastRead < tstampThreshold) {
                    this.delete(key);
                }
            });
        }

        // Loop through and destroy all items that have remained static for
        // longer than the specified "time to live"
        if (this.ttl !== undefined) {
            const tstampThreshold = hrtime() - this.ttl;

            this.lastWrite.forEach((lastWrite, key) => {
                if (lastWrite < tstampThreshold) {
                    this.delete(key);
                }
            });
        }

        // If the store has passed it's capacity, then delete the oldest entires
        // immediately
        const capacityExceededBy = this.store.size - this.capacity;

        if (capacityExceededBy > 0) {
            const oldEntries = Array.from(this.lastRead.entries())
                .sort((a, b) => a[1] - b[1]) // Sort by latest access, ascending
                .slice(0, capacityExceededBy); // Grab entries that exceed capacity

            for (const [entry] of oldEntries) {
                this.delete(entry);
            }
        }

        // Run gb again in one minute
        clearTimeout(this.garbageCollectionTimeout);

        this.garbageCollectionTimeout = setTimeout(this.garbageCollect, 60000);
    }
}
